// src/firebase/services.js - VERSÃO CORRIGIDA
import { getFirestoreInstance } from './config';

// ============== FUNÇÕES AUXILIARES ==============

// Obter instância do Firestore
const getDb = async () => {
  try {
    const db = await getFirestoreInstance();
    if (!db) {
      throw new Error('Firestore não disponível');
    }
    return db;
  } catch (error) {
    console.error('❌ Erro ao obter Firestore:', error);
    throw error;
  }
};

// Importar funções do Firestore dinamicamente
const getFirestoreFunctions = async () => {
  const {
    collection,
    addDoc,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
  } = await import('firebase/firestore');
  
  return {
    collection,
    addDoc,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
  };
};

// ============== AGENDAMENTOS ==============

export const saveAppointment = async (appointmentData) => {
  try {
    const db = await getDb();
    const { collection, addDoc, serverTimestamp } = await getFirestoreFunctions();
    
    const appointmentWithMetadata = {
      ...appointmentData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'confirmado'
    };

    const docRef = await addDoc(collection(db, 'appointments'), appointmentWithMetadata);
    
    console.log('✅ Agendamento salvo no Firebase com ID:', docRef.id);
    return { id: docRef.id, ...appointmentWithMetadata };
  } catch (error) {
    console.error('❌ Erro ao salvar agendamento:', error);
    throw error;
  }
};

export const getAppointmentsByPhone = async (phone) => {
  try {
    const db = await getDb();
    const { collection, query, where, orderBy, getDocs } = await getFirestoreFunctions();
    
    const q = query(
      collection(db, 'appointments'),
      where('telefone', '==', phone),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const appointments = [];
    
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    
    return appointments;
  } catch (error) {
    console.error('❌ Erro ao buscar agendamentos:', error);
    return [];
  }
};

export const getAllAppointments = async () => {
  try {
    const db = await getDb();
    const { collection, query, orderBy, getDocs } = await getFirestoreFunctions();
    
    const q = query(
      collection(db, 'appointments'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const appointments = [];
    
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    
    return appointments;
  } catch (error) {
    console.error('❌ Erro ao buscar todos agendamentos:', error);
    return [];
  }
};

export const updateAppointmentStatus = async (appointmentId, newStatus) => {
  try {
    const db = await getDb();
    const { doc, updateDoc, serverTimestamp } = await getFirestoreFunctions();
    
    const appointmentRef = doc(db, 'appointments', appointmentId);
    await updateDoc(appointmentRef, {
      status: newStatus,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Status do agendamento atualizado:', appointmentId);
    return true;
  } catch (error) {
    console.error('❌ Erro ao atualizar agendamento:', error);
    throw error;
  }
};

export const cancelAppointment = async (appointmentId) => {
  return updateAppointmentStatus(appointmentId, 'cancelado');
};

// ============== SERVIÇOS ==============

export const getServices = async () => {
  try {
    const db = await getDb();
    const { collection, getDocs } = await getFirestoreFunctions();
    
    const querySnapshot = await getDocs(collection(db, 'services'));
    const services = [];
    
    querySnapshot.forEach((doc) => {
      services.push({ id: doc.id, ...doc.data() });
    });
    
    return services;
  } catch (error) {
    console.error('❌ Erro ao buscar serviços:', error);
    // Retorna serviços padrão como fallback
    return getDefaultServices();
  }
};

const getDefaultServices = () => [
  { id: '1', nome: 'Corte de Cabelo', descricao: 'Corte moderno', preco: 30, duracao: 30, ativo: true },
  { id: '2', nome: 'Barba', descricao: 'Aparação completa', preco: 20, duracao: 20, ativo: true },
  { id: '3', nome: 'Corte + Barba', descricao: 'Combo completo', preco: 45, duracao: 50, ativo: true },
];

// ============== PAGAMENTOS ==============

export const createPixPayment = async (paymentData) => {
  try {
    const db = await getDb();
    const { collection, addDoc, serverTimestamp } = await getFirestoreFunctions();
    
    const paymentWithMetadata = {
      ...paymentData,
      createdAt: serverTimestamp(),
      status: 'pendente',
      tipo: 'pix'
    };

    const docRef = await addDoc(collection(db, 'payments'), paymentWithMetadata);
    
    console.log('✅ Pagamento PIX criado com ID:', docRef.id);
    return { id: docRef.id, ...paymentWithMetadata };
  } catch (error) {
    console.error('❌ Erro ao criar pagamento PIX:', error);
    throw error;
  }
};

export const updatePaymentStatus = async (paymentId, newStatus, txid = null) => {
  try {
    const db = await getDb();
    const { doc, updateDoc, serverTimestamp } = await getFirestoreFunctions();
    
    const paymentRef = doc(db, 'payments', paymentId);
    const updateData = {
      status: newStatus,
      updatedAt: serverTimestamp()
    };
    
    if (txid) {
      updateData.txid = txid;
    }
    
    await updateDoc(paymentRef, updateData);
    
    console.log('✅ Status do pagamento atualizado:', paymentId);
    return true;
  } catch (error) {
    console.error('❌ Erro ao atualizar pagamento:', error);
    throw error;
  }
};

// ============== SISTEMA DE BACKUP ==============

export const syncLocalWithFirebase = async (localData, phone) => {
  try {
    console.log('🔄 Sincronizando dados do localStorage com Firebase...');
    
    // Busca agendamentos locais
    const localAppointments = JSON.parse(localStorage.getItem('barbearia_agendamentos') || '[]');
    const localForUser = localAppointments.filter(app => app.telefone === phone);
    
    // Para cada agendamento local, salva no Firebase
    for (const appointment of localForUser) {
      // Verifica se já existe no Firebase
      const firebaseAppointments = await getAppointmentsByPhone(phone);
      const exists = firebaseAppointments.some(fbApp => 
        fbApp.data === appointment.data && 
        fbApp.hora === appointment.hora
      );
      
      if (!exists && appointment.id && !appointment.id.startsWith('firebase_')) {
        await saveAppointment(appointment);
      }
    }
    
    console.log('✅ Sincronização completa!');
    return true;
  } catch (error) {
    console.error('❌ Erro na sincronização:', error);
    return false;
  }
};

// ============== FUNÇÃO DE TESTE ==============

export const testFirestoreConnection = async () => {
  try {
    const db = await getDb();
    const { collection, getDocs } = await getFirestoreFunctions();
    
    const querySnapshot = await getDocs(collection(db, 'services'));
    console.log('✅ Firestore conectado! Serviços encontrados:', querySnapshot.size);
    
    return {
      success: true,
      servicesCount: querySnapshot.size,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Erro no teste do Firestore:', error);
    return {
      success: false,
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
};